import React from 'react'
import { Controller, FieldError, Path, FieldValues, Control } from 'react-hook-form'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MaskedFormFieldProps<TFieldValues extends FieldValues> {
  label: string
  name: Path<TFieldValues>
  control: Control<TFieldValues>
  error?: FieldError
  required?: boolean
  placeholder?: string
  disabled?: boolean
  helperText?: string
  className?: string
  maskFunction: (value: string) => string
}

export function MaskedFormField<TFieldValues extends FieldValues>({
  label,
  name,
  control,
  error,
  required,
  placeholder,
  disabled,
  helperText,
  className,
  maskFunction,
}: MaskedFormFieldProps<TFieldValues>) {
  return (
    <div className={className}>
      <label htmlFor={String(name)} className="block text-sm font-medium text-brand-gray mb-2">
        {label} {required && <span className="text-error">*</span>}
      </label>

      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <input
            ref={field.ref}
            name={field.name}
            id={String(name)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(
              'form-input w-full px-3 py-2 border border-brand-gray/30 rounded-monumenta',
              'bg-brand-dark-lighter text-white placeholder-brand-gray',
              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
              'hover:border-brand-gray/50 transition-colors duration-200',
              error && 'border-error focus:ring-error focus:border-error'
            )}
            onChange={(e) => {
              const maskedValue = maskFunction(e.target.value)
              console.log(`[${String(name)}] Input value:`, e.target.value, '-> Masked:', maskedValue)
              field.onChange(maskedValue)
            }}
            onBlur={field.onBlur}
            value={field.value || ''}
          />
        )}
      />

      {error && (
        <div className="flex items-center gap-2 mt-2 text-error text-sm">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-brand-gray mt-1">{helperText}</p>
      )}
    </div>
  )
}
